/**
 * Replay of one simulated basket: the BTC index path with entry / adjustment / exit markers,
 * every modelled fill (price vs mark, half-spread, fee, slippage, depth levels) and every
 * decision with the alternatives the engine ranked at that instant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CartesianGrid, ComposedChart, Line, ReferenceDot, Tooltip, XAxis, YAxis } from 'recharts';
import { optionsimService, simError, SimReplay } from '../../services/optionsim.service';
import { AltTable, ChartCard, Stat, StatGrid, useChartChrome, useIsPhone } from './simUi';
import { UTIL_HINT, dateTimeUtc, dateUtc, inrText, legLabel, money, num, signedMoney, signedUsd, usd, utilPct } from './simFormat';

type MarkerKind = 'entry' | 'adjust' | 'exit';
const MARKER_LABEL: Record<MarkerKind, string> = { entry: 'Entry', adjust: 'Adjustment', exit: 'Exit' };

/** Marker glyphs: shape carries the kind (never colour alone). */
const Glyph: React.FC<{ cx?: number; cy?: number; kind: MarkerKind; fill: string; ring: string }> = ({ cx = 0, cy = 0, kind, fill, ring }) => {
  if (kind === 'entry') return <path d={`M${cx},${cy - 6} L${cx + 6},${cy + 5} L${cx - 6},${cy + 5} Z`} fill={fill} stroke={ring} strokeWidth={2} />;
  if (kind === 'exit') return <rect x={cx - 5} y={cy - 5} width={10} height={10} rx={2} fill={fill} stroke={ring} strokeWidth={2} />;
  return <path d={`M${cx},${cy - 6} L${cx + 6},${cy} L${cx},${cy + 6} L${cx - 6},${cy} Z`} fill={fill} stroke={ring} strokeWidth={2} />;
};

export const CycleReplay: React.FC<{ open: boolean; onClose: () => void; runId: number; keyId: string; cycle: number; label: string; fx: number }> = ({ open, onClose, runId, keyId, cycle, label, fx }) => {
  const phone = useIsPhone();
  const { C, axis, grid, tooltip } = useChartChrome();
  const [data, setData] = useState<SimReplay | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let dead = false;
    setData(null); setErr(null);
    optionsimService.replay(runId, keyId, cycle)
      .then(d => { if (!dead) setData(d); })
      .catch(e => { if (!dead) setErr(simError(e)); });
    return () => { dead = true; };
  }, [open, runId, keyId, cycle]);

  const s = data?.summary ?? null;
  const markers = useMemo(() => {
    if (!data) return [] as Array<{ t: number; spot: number; kind: MarkerKind; text: string }>;
    const byT = new Map<number, { t: number; spot: number; kind: MarkerKind; text: string[] }>();
    for (const f of data.fills) {
      const kind: MarkerKind = /^entry/i.test(f.reason) ? 'entry' : s && f.t >= s.exitT ? 'exit' : 'adjust';
      const m = byT.get(f.t) ?? { t: f.t, spot: f.spot, kind, text: [] };
      if (kind === 'exit' || (kind === 'adjust' && m.kind === 'entry')) m.kind = kind;
      m.text.push(`${f.side} ${num(f.qty, 0)} ${legLabel(f.symbol)} @ $${num(f.px, 1)}`);
      byT.set(f.t, m);
    }
    for (const d of data.decisions) {
      const m = byT.get(d.t) ?? { t: d.t, spot: d.spot, kind: 'adjust' as MarkerKind, text: [] };
      m.text.unshift(`${d.action}${d.trigger ? ` (${d.trigger})` : ''}`);
      if (m.kind !== 'entry' && m.kind !== 'exit') m.kind = 'adjust';
      byT.set(d.t, m);
    }
    // a basket held to settlement has no exit fill: mark the settlement itself
    if (s && !Array.from(byT.values()).some(m => m.kind === 'exit')) {
      byT.set(s.exitT, { t: s.exitT, spot: s.endSpot, kind: 'exit', text: [s.stopped ? `stopped${s.closeReason ? `: ${s.closeReason}` : ''}` : s.closeReason ?? 'settled'] });
    }
    return Array.from(byT.values()).sort((a, b) => a.t - b.t).map(m => ({ ...m, text: m.text.slice(0, 6).join('\n') }));
  }, [data, s]);

  // ticks on whole UTC hours / days (auto ticks on a numeric time axis land on odd minutes)
  const ticks = useMemo(() => {
    const idx = data?.index ?? [];
    if (idx.length < 2) return undefined;
    const t0 = idx[0][0], t1 = idx[idx.length - 1][0], span = t1 - t0;
    const step = span <= 2 * 86400 ? 6 * 3600 : span <= 9 * 86400 ? 86400 : 2 * 86400;
    const out: number[] = [];
    for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) out.push(t);
    return out;
  }, [data]);

  // The hourly index drives both axes. Markers are reference dots on it, and each hour's row
  // carries the actions inside that hour for the tooltip (index points are stamped at bar close).
  const line = useMemo(() => {
    const rows: Array<{ t: number; spot: number; notes: string[] }> = (data?.index ?? []).map(([t, v]) => ({ t, spot: v, notes: [] }));
    for (const m of markers) {
      const i = rows.findIndex(r => r.t >= m.t);
      if (i >= 0) rows[i].notes.push(`${MARKER_LABEL[m.kind]} ${dateTimeUtc(m.t)}: ${m.text.split('\n').join('; ')}`);
    }
    return rows;
  }, [data, markers]);
  const kinds: MarkerKind[] = ['entry', 'adjust', 'exit'];
  const markerFill: Record<MarkerKind, string> = { entry: C.chartSeries[0], adjust: C.chartSeries[3], exit: C.chartSeries[6] };
  const ring = C.chartTooltipBg;
  const tone = (v: number) => (v > 0 ? C.bull : v < 0 ? C.bear : undefined);
  const tx = (data?.events ?? []).filter(e => e.type !== 'fill' && e.type !== 'decision');

  return (
    <Dialog open={open} onClose={onClose} fullScreen={phone} maxWidth="lg" fullWidth data-testid="optionsim-replay">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="subtitle1" fontWeight={800} component="div" sx={{ overflowWrap: 'anywhere' }}>{label} · cycle {cycle}</Typography>
        {s && (
          <Typography variant="caption" color="text.secondary" component="div">
            {dateTimeUtc(s.entryT)} → {dateTimeUtc(s.exitT)} UTC · expiry {dateTimeUtc(s.expiry)} · {s.stopped ? `stopped${s.closeReason ? `: ${s.closeReason}` : ''}` : s.closeReason ?? 'held to settlement'}
          </Typography>
        )}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 } }}>
        {!data && !err && <LinearProgress />}
        {err && <Alert severity="error">{err}</Alert>}
        {data && s && (
          <Stack spacing={2}>
            <StatGrid min={124}>
              <Stat label="P&L" value={signedMoney(s.pnlUsd, fx)} tone={s.pnlUsd >= 0 ? 'bull' : 'bear'} hint="After fees and modelled slippage." />
              <Stat label="Credit" value={money(s.creditUsd, fx)} hint="Net premium taken in at entry." />
              <Stat label="Before costs" value={signedMoney(s.grossPnlUsd, fx)} />
              <Stat label="Fees + slippage" value={money(s.feesUsd + s.slippageUsd, fx)} hint={`Fees ${money(s.feesUsd, fx)} · slippage ${money(s.slippageUsd, fx)}`} />
              <Stat label="Adjustments" value={num(s.adjustments, 0)} hint={`Cost ${money(s.adjustmentCostUsd, fx)}`} />
              <Stat label="Worst MTM" value={signedMoney(s.worstMtmUsd, fx)} tone={s.worstMtmUsd < 0 ? 'bear' : null} />
              <Stat label="Path" value={`${num(s.minPct, 1)}% / +${num(s.maxPct, 1)}%`} hint="Lowest / highest index move from entry." />
              <Stat label="Max margin" value={s.equityExhausted ? 'exhausted' : utilPct(s.maxMarginUtilPct)} hint={`Peak initial margin ${money(s.maxImUsd, fx)}. ${UTIL_HINT}`} tone={s.marginBreaches > 0 || s.equityExhausted ? 'warn' : null} />
            </StatGrid>

            {line.length > 1 ? (
              <ChartCard title="BTC index (.DEXBTUSD, hourly)" height={phone ? 220 : 280} testId="optionsim-replay-chart"
                subtitle="Hourly closes. Each marker sits at the index of its own 5-minute bar, so a marker inside a fast wick can sit off the line."
                legend={(
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {kinds.map(k => (
                      <Stack key={k} direction="row" spacing={0.5} alignItems="center">
                        <svg width={14} height={14} aria-hidden><Glyph cx={7} cy={7} kind={k} fill={markerFill[k]} ring={ring} /></svg>
                        <Typography variant="caption" color="text.secondary">{MARKER_LABEL[k]}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}>
                <ComposedChart data={line} margin={{ top: 10, right: 12, bottom: 0, left: 4 }}>
                  <CartesianGrid {...grid} />
                  <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} ticks={ticks}
                    tickFormatter={t => (ticks && ticks.length > 1 && ticks[1] - ticks[0] >= 86400 ? dateUtc(t).replace(/ \d{4}$/, '') : dateTimeUtc(t))} minTickGap={24} {...axis} />
                  <YAxis domain={['auto', 'auto']} tickFormatter={v => `$${num(v / 1000, 1)}K`} width={58} {...axis} />
                  <Tooltip {...tooltip} content={<ReplayTip />} />
                  <Line dataKey="spot" name="Index" stroke={C.chartLine1} strokeWidth={2} dot={false} isAnimationActive={false} />
                  {markers.map((m, i) => (
                    <ReferenceDot key={`${m.t}-${i}`} x={m.t} y={m.spot} ifOverflow="extendDomain"
                      shape={(p: any) => <Glyph cx={p.cx} cy={p.cy} kind={m.kind} fill={markerFill[m.kind]} ring={ring} />} />
                  ))}
                </ComposedChart>
              </ChartCard>
            ) : (
              <Alert severity="info">No hourly index stored for this window, so the path chart is not available.</Alert>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Fills ({num(data.fills.length, 0)})</Typography>
              {phone ? (
                <Stack spacing={0.5} data-testid="optionsim-replay-fills">
                  {data.fills.map((f, i) => (
                    <Box key={`${f.t}-${i}`} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        <Box component="span" sx={{ color: 'text.secondary' }}>{dateTimeUtc(f.t)}</Box>{' · '}
                        <Box component="span" sx={{ color: f.side === 'SELL' ? C.bear : C.bull, fontWeight: 700 }}>{f.side}</Box>{' '}
                        {num(f.qty, 0)} {legLabel(f.symbol)} @ {usd(f.px, 2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                        mark {usd(f.mark, 2)} · fee {usd(f.feeUsd, 2)} · slippage {usd(f.slippageUsd, 2)} · {f.market ? 'market' : `${num(f.levels, 0)} level${f.levels === 1 ? '' : 's'}`} · {f.reason}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" data-testid="optionsim-replay-fills" sx={{ '& td, & th': { px: 0.75, py: 0.4, fontSize: '0.72rem', whiteSpace: 'nowrap' }, '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                  <TableHead>
                    <TableRow>
                      {['Time (UTC)', 'Leg', 'Side', 'Qty', 'Price', 'Mark', 'Half-spread', 'Fee', 'Slippage', 'Fill', 'Why'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700 }} align={['Qty', 'Price', 'Mark', 'Half-spread', 'Fee', 'Slippage'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.fills.map((f, i) => (
                      <TableRow key={`${f.t}-${i}`}>
                        <TableCell>{dateTimeUtc(f.t)}</TableCell>
                        <TableCell>{legLabel(f.symbol)}</TableCell>
                        <TableCell sx={{ color: f.side === 'SELL' ? C.bear : C.bull, fontWeight: 700 }}>{f.side}</TableCell>
                        <TableCell align="right">{num(f.qty, 0)}</TableCell>
                        <TableCell align="right">{usd(f.px, 2)}</TableCell>
                        <TableCell align="right">{usd(f.mark, 2)}</TableCell>
                        <TableCell align="right">{usd(f.halfSpread, 2)}</TableCell>
                        <TableCell align="right">{usd(f.feeUsd, 2)}</TableCell>
                        <TableCell align="right">{usd(f.slippageUsd, 2)}</TableCell>
                        <TableCell>{f.market ? 'market' : `${num(f.levels, 0)} level${f.levels === 1 ? '' : 's'}`}</TableCell>
                        <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.reason}>{f.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              )}
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Prices are modelled around the MARK (no historical bid/ask exists); slippage = |price − mark| × size, in USD.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Decisions ({num(data.decisions.length, 0)})</Typography>
              {!data.decisions.length && <Typography variant="body2" color="text.secondary">No adjustment decisions in this cycle.</Typography>}
              {data.decisions.map((d, i) => (
                <Accordion key={`${d.t}-${i}`} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={800} component="div" sx={{ overflowWrap: 'anywhere' }}>{dateTimeUtc(d.t)} · {d.action}</Typography>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere' }}>
                        {d.trigger} · spot ${num(d.spot, 0)}{d.costUsd != null ? ` · cost ${money(d.costUsd, fx)}` : ''}{d.counted === false ? ' · not counted' : ''}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        Δ {num(d.greeksBefore.delta, 3)} → {num(d.greeksAfter.delta, 3)} BTC · Γ/1% {num(d.greeksBefore.gammaPer1Pct, 3)} → {num(d.greeksAfter.gammaPer1Pct, 3)} · vega {signedUsd(d.greeksBefore.vega)} → {signedUsd(d.greeksAfter.vega)} · θ/day {signedUsd(d.greeksBefore.theta)} → {signedUsd(d.greeksAfter.theta)}
                      </Typography>
                      {d.snapBackBeforeUsd != null && d.snapBackAfterUsd != null && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          If the index snapped back to the entry level: {signedMoney(d.snapBackBeforeUsd, fx)} before → <Box component="span" sx={{ color: tone(d.snapBackAfterUsd) }}>{signedMoney(d.snapBackAfterUsd, fx)}</Box> after.
                        </Typography>
                      )}
                      {d.note && <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{inrText(d.note, fx)}</Typography>}
                      {d.alternatives && d.alternatives.length > 0 && <AltTable rows={d.alternatives} fx={fx} max={12} />}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {tx.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>Other events</Typography>
                <Stack spacing={0.5}>
                  {tx.slice(0, 200).map((e, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                      <Chip size="small" label={e.type} sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{e.t ? dateTimeUtc(e.t) : ''}</Typography>
                      <Typography variant="caption" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{eventText(e, fx)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
        {data && !s && <Alert severity="warning">This cycle has no summary (the run may have been cancelled mid-cycle).</Alert>}
      </DialogContent>
    </Dialog>
  );
};

/** Tooltip: the hour's index close and every action inside that hour. */
const ReplayTip: React.FC<any> = ({ active, payload }) => {
  const { tooltip } = useChartChrome();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  return (
    <Box sx={{ ...tooltip.contentStyle, p: 1, maxWidth: 360 }}>
      <Typography variant="caption" component="div" sx={{ fontWeight: 700, color: tooltip.labelStyle.color }}>{dateTimeUtc(row.t)} UTC</Typography>
      <Typography variant="caption" component="div">Index ${num(row.spot, 0)}</Typography>
      {row.notes.map((n: string, i: number) => <Typography key={i} variant="caption" component="div" sx={{ overflowWrap: 'anywhere' }}>{n}</Typography>)}
    </Box>
  );
};

function eventText(e: any, fx: number): string {
  if (e.type === 'note') return e.text;
  if (e.type === 'settle') return `${num(Math.abs(e.qty), 0)} ${e.qty < 0 ? 'short' : 'long'} ${legLabel(e.symbol ?? '')} settled at $${num(e.value, 2)}${e.inferred ? ' (inferred from the index)' : ''} · ${signedMoney(e.cashUsd, fx)}${e.feeUsd ? ` · fee ${money(e.feeUsd, fx)}` : ''}`;
  if (e.type === 'funding') return `perp funding ${num(e.ratePctPer8h, 4)}% / 8 h on ${num(e.perpContracts, 0)} contracts · ${signedMoney(e.cashUsd, fx)}`;
  if (e.type === 'unfilled') return `${e.side} ${num(e.qty, 0)} ${legLabel(e.symbol ?? '')} not filled: ${e.why}`;
  return JSON.stringify(e).slice(0, 200);
}

export default CycleReplay;
